import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { KioskShell } from "../components/KioskShell";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { Search, Gauge, Fuel, Cog } from "lucide-react";

export default function KioskHomePage() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [makeFilter, setMakeFilter] = useState("all");
  const [bodyFilter, setBodyFilter] = useState("all");
  const [priceMax, setPriceMax] = useState([350000]);

  useEffect(() => {
    api.get("/vehicles", { params: { status: "available" } }).then((r) => {
      setVehicles(r.data || []);
      setLoading(false);
    });
  }, []);

  const makes = useMemo(
    () => Array.from(new Set(vehicles.map((v) => v.make))).sort(),
    [vehicles],
  );
  const bodies = useMemo(
    () => Array.from(new Set(vehicles.map((v) => v.body_type))).sort(),
    [vehicles],
  );

  const filtered = vehicles.filter((v) => {
    if (makeFilter !== "all" && v.make !== makeFilter) return false;
    if (bodyFilter !== "all" && v.body_type !== bodyFilter) return false;
    if (v.price > priceMax[0]) return false;
    if (q) {
      const s = q.toLowerCase();
      if (
        !`${v.make} ${v.model} ${v.year} ${v.vin}`.toLowerCase().includes(s)
      )
        return false;
    }
    return true;
  });

  return (
    <KioskShell showExit={true}>
      {/* Hero strip */}
      <section className="hero-bg stripe-overlay py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="brand-divider mb-4 max-w-md">
            <span className="dot" />
            <span className="label-eyebrow" style={{ color: "var(--wakzy-gold)" }}>
              Step 1 · Vehicle Selection
            </span>
            <span className="dot" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light leading-tight max-w-3xl mb-3">
            <span className="chrome-text">Find your</span> <span className="gold-text">next drive.</span>
          </h1>
          <p className="text-slate-300 max-w-xl">
            Browse our hand-picked luxury inventory. Tap a vehicle to begin the
            paperless purchase experience.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 -mt-10 relative z-10">
        <div className="glass rounded-md p-5 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div className="md:col-span-1">
            <div className="label-eyebrow mb-2">Search</div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Make, model, VIN"
                className="pl-10 h-11 bg-[#030712] border-white/10"
                data-testid="kiosk-search-input"
              />
            </div>
          </div>
          <div>
            <div className="label-eyebrow mb-2">Make</div>
            <Select value={makeFilter} onValueChange={setMakeFilter}>
              <SelectTrigger className="h-11 bg-[#030712] border-white/10" data-testid="kiosk-make-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All makes</SelectItem>
                {makes.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="label-eyebrow mb-2">Body type</div>
            <Select value={bodyFilter} onValueChange={setBodyFilter}>
              <SelectTrigger className="h-11 bg-[#030712] border-white/10" data-testid="kiosk-body-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All bodies</SelectItem>
                {bodies.map((b) => (
                  <SelectItem key={b} value={b}>
                    {b}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="label-eyebrow mb-2">
              Max price · ${priceMax[0].toLocaleString()}
            </div>
            <Slider
              value={priceMax}
              onValueChange={setPriceMax}
              min={50000}
              max={350000}
              step={5000}
              data-testid="kiosk-price-slider"
            />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-6">
          <div className="label-eyebrow">
            {filtered.length} vehicle{filtered.length === 1 ? "" : "s"} available
          </div>
        </div>
        {loading ? (
          <div className="text-slate-400 text-sm">Loading inventory…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((v, i) => (
              <article
                key={v.id}
                className="surface hover-lift overflow-hidden cursor-pointer group fade-up rounded-md relative"
                style={{ animationDelay: `${Math.min(i * 60, 600)}ms` }}
                onClick={() => navigate(`/kiosk/vehicle/${v.id}`)}
                data-testid={`vehicle-card-${v.id}`}
              >
                {/* Red racing stripe — echoes the logo's red car silhouette */}
                <div
                  className="absolute top-0 left-0 right-0 h-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent 0%, #e63946 25%, #d4af37 75%, transparent 100%)",
                  }}
                />
                <div
                  className="aspect-[16/10] bg-cover bg-center relative"
                  style={{ backgroundImage: `url(${v.images?.[0] || "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1200"})` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0b1324] via-transparent to-transparent opacity-80" />
                  <div className="absolute top-3 left-3">
                    <span className="pill pill-available">{v.status}</span>
                  </div>
                  <div
                    className="absolute bottom-3 right-3 px-3 py-1.5 rounded font-heading text-lg"
                    style={{
                      background: "linear-gradient(135deg, rgba(212,175,55,0.95), rgba(230,57,70,0.85))",
                      color: "#0a0a0a",
                      fontWeight: 800,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                    }}
                  >
                    ${v.price.toLocaleString()}
                  </div>
                </div>
                <div className="p-5">
                  <div className="text-xs text-slate-400">{v.year} · {v.color}</div>
                  <h3 className="font-heading text-xl mt-1 mb-2 chrome-text inline-block">{v.make} {v.model}</h3>
                  <div className="flex items-center gap-4 text-xs text-slate-400">
                    <span className="flex items-center gap-1"><Gauge className="w-3 h-3" /> {v.mileage?.toLocaleString()} mi</span>
                    <span className="flex items-center gap-1"><Fuel className="w-3 h-3" /> {v.fuel_type}</span>
                    <span className="flex items-center gap-1"><Cog className="w-3 h-3" /> {v.transmission}</span>
                  </div>
                  <Button
                    className="btn-gold rounded-sm w-full mt-4 h-10 text-sm"
                    data-testid={`vehicle-select-${v.id}`}
                  >
                    View details
                  </Button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </KioskShell>
  );
}
